import { useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck } from "lucide-react";
import { shrinkImage } from "@/lib/shrinkImage";
import { KycFields } from "@/components/KycFields";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Public page — the customer scans the counter QR code and fills this in themselves. */
export default function KycForm() {
  const { branch } = useParams();
  const [form, setForm] = useState({
    name_as_per_passport: "",
    nationality: "",
    licence_number: "",
    licence_state: "",
    licence_expiry: "",
    passport_number: "",
    passport_issue: "",
    passport_expiry: "",
    company_name: "",
    abn: "",
    acn: "",
    address: "",
    email: "",
    whatsapp_mobile: "",
    customer_name: "",
  });
  const [accepted, setAccepted] = useState(false);
  const [privacyOk, setPrivacyOk] = useState(false);
  const [signature, setSignature] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (file) => {
    setUploading(true);
    try {
      const prepared = await shrinkImage(file);
      const body = new FormData();
      body.append("file", prepared);
      const { data } = await axios.post(`${API}/public/kyc/upload`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFiles((f) => [
        ...f,
        {
          file_id: data.file_id,
          kind: "kyc_id",
          filename: data.filename || prepared.name,
          content_type: data.content_type,
          size: data.size,
        },
      ]);
      toast.success("Document attached");
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not attach that file"
      );
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.name_as_per_passport.trim()) return toast.error("Please enter your name as per passport");
    if (!form.customer_name.trim()) return toast.error("Please enter your name at the bottom");
    if (!form.whatsapp_mobile.trim()) return toast.error("Please enter your WhatsApp mobile number");
    if (!form.email.trim()) return toast.error("Please enter your email address");
    if (!privacyOk) return toast.error("Please confirm you have read the privacy notice");
    if (!accepted) return toast.error("Please accept the declaration");
    if (files.length < 2) return toast.error("Please attach photos of two forms of ID");
    setSaving(true);
    try {
      await axios.post(`${API}/public/kyc`, {
        ...form,
        branch: (branch || "").toUpperCase(),
        declaration_accepted: accepted,
        privacy_accepted: privacyOk,
        signature,
        attachments: files,
      });
      setDone(true);
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not send that form"
      );
    } finally {
      setSaving(false);
    }
  };

  if (done)
    return (
      <Shell>
        <div className="py-10 text-center" data-testid="kyc-done">
          <BadgeCheck className="mx-auto h-12 w-12 text-[#166534]" />
          <h1 className="mt-4 text-2xl font-bold">Thank you</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Your details have been sent to Dhunna Jewellers. If you gave an email address, a copy
            is on its way to you.
          </p>
        </div>
      </Shell>
    );

  return (
    <Shell>
      <h1 className="font-invoice text-2xl font-bold">Know Your Customer (KYC)</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        {(branch || "").toString().toUpperCase()} store · required for anti-money-laundering
        record keeping
      </p>

      <div className="mt-6 space-y-4" data-testid="kyc-form">
        <KycFields
          form={form}
          set={set}
          files={files}
          onRemoveFile={(i) => setFiles(files.filter((_, j) => j !== i))}
          upload={upload}
          uploading={uploading}
          signature={signature}
          setSignature={setSignature}
          accepted={accepted}
          setAccepted={setAccepted}
          privacyOk={privacyOk}
          setPrivacyOk={setPrivacyOk}
          branch={branch}
        />

        <Button
          className="w-full rounded-none bg-[#C5A059] py-6 font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
          onClick={submit}
          disabled={saving}
          data-testid="kyc-submit-button"
        >
          {saving ? "Sending…" : "Send my details"}
        </Button>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#FBF8F2] px-4 py-8">
    <div className="mx-auto max-w-2xl border border-border bg-white p-6 sm:p-8">
      <div className="mb-6 border-b border-border pb-4 text-center">
        <p className="font-invoice text-xl font-bold tracking-wide">DHUNNA JEWELLERS</p>
        <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          Gold · Diamond · Silver
        </p>
      </div>
      {children}
    </div>
  </div>
);
